import { css, Styles, withStyles } from '@recruit-robin/robin-theme/web-styles';

import { af, bareButtonCss, compactQuery, focusRingCss } from '../theme';

/**
 * Ported from `.howpick`, `.chan`, `.chan-opt` and `.callout` in
 * `.temp/change-my-interview-v2/change-my-interview.html`. Those classes are marked "original to this
 * screen" in the handover, so none of them is the method screen's card or the question screen's row —
 * the values below are that file's, not a reinterpretation of them.
 */
const styles = {
  /** `.howpick` — 40px clear of the calendar underneath it. */
  root: css`
    margin-bottom: ${af.sp10};
  `,

  label: css`
    margin-bottom: 10px;
  `,

  /**
   * `.chan` — and side by side on a phone too.
   *
   * The prototype is explicit about not stacking these: "two cards read as one choice, stacked they
   * read as a list you scroll past." Only the gap and the padding tighten on compact.
   */
  options: css`
    display: flex;
    gap: 8px;

    ${compactQuery} {
      gap: 6px;
    }
  `,

  /**
   * `.chan-opt` — a 1px hairline and a 14px radius, like the slots, not the 20px shadowed surfaces.
   * Icon above the label rather than a radio beside it.
   */
  option: css`
    ${bareButtonCss};
    ${focusRingCss};
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 3px;
    border: 1px solid ${af.line};
    border-radius: 14px;
    background: ${af.paper};
    padding: 16px 18px;
    text-align: left;
    color: ${af.ink};
    transition:
      border-color 0.12s ease,
      background 0.12s ease;

    &:hover {
      border-color: ${af.muted};
    }

    ${compactQuery} {
      padding: 14px;
    }
  `,

  /**
   * The ring replaces the border rather than sitting on it.
   *
   * Straight from the prototype's own note: these keep a 1px border when unselected, unlike the rows
   * and cards whose surfaces have none, so an inset 2px ring stacked on top of it reads as 3px.
   */
  optionSelected: css`
    box-shadow: inset 0 0 0 2px ${af.ink};
    background: ${af.paper};
    border-color: transparent;

    &:hover {
      border-color: transparent;
    }
  `,

  optionIcon: css`
    display: flex;
    align-items: center;
    color: ${af.ink};
    margin-bottom: 7px;
  `,

  /**
   * `.callout` — a flat `--fill` panel, not a control.
   *
   * "The number is the one thing on this screen the candidate has to act on, so it gets a surface of
   * its own rather than sitting in a grey note."
   */
  callout: css`
    margin-top: ${af.sp4};
    padding: 14px 18px;
    background: ${af.fill};
    border-radius: ${af.rCard};
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 11px;
    text-align: left;
  `,

  calloutEditing: css`
    gap: 9px;
  `,

  calloutIcon: css`
    flex: 0 0 auto;
    display: flex;
    color: ${af.ink};
  `,

  /** The number itself, never wrapped — and tabular so editing it does not reflow the row. */
  number: css`
    color: ${af.ink};
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  `,

  /** `.phone-input` — paper on the fill panel, no border of its own. */
  input: css`
    ${focusRingCss};
    flex: 1;
    min-width: 0;
    appearance: none;
    -webkit-appearance: none;
    margin: 0;
    font: inherit;
    /* 16px, so iOS does not zoom the viewport and scroll the calendar out of view on focus. */
    font-size: 16px;
    font-weight: 700;
    color: ${af.ink};
    border: none;
    border-radius: 13px;
    background: ${af.paper};
    padding: 10px 13px;
    font-variant-numeric: tabular-nums;
  `,

  /**
   * `.phone-save` — an ink pill, the one filled control inside the panel. Its label is the CTA
   * role: the kit sets every button in Small, Black capitals.
   */
  save: css`
    ${bareButtonCss};
    ${focusRingCss};
    flex: 0 0 auto;
    border-radius: ${af.rPill};
    background: ${af.ink};
    color: ${af.paper};
    padding: 9px 15px;
  `,

  /** `.phone-edit` — the same pencil affordance the review screen uses to change an answer. */
  edit: css`
    ${bareButtonCss};
    ${focusRingCss};
    flex: 0 0 auto;
    border-radius: ${af.rPill};
    width: 34px;
    height: 34px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${af.muted};
    transition:
      background 0.12s ease,
      color 0.12s ease;

    &:hover {
      background: ${af.paper};
      color: ${af.ink};
    }
  `,

  /** The refusal, under the panel the number sits in. */
  error: css`
    margin-top: ${af.sp2};
  `,
} satisfies Styles<void>;

export const useStyles = withStyles(styles);
